import { useRulesDetailModalContext } from 'contexts/RulesDetailModalContext';
import { useFormikContext } from 'formik';
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';

import {
  PartFileUnits,
  type CountOperator,
  type RangeFilterValue,
} from '@frontend/api';
import {
  ArrowDownRightIcon,
  Button,
  Flex,
  HStack,
  VStack,
  DragHandleIcon,
  IconButton,
  type BoxProps,
  Tooltip,
} from '@frontend/legacy-components';

import { ItemAndDropBorderContainer } from 'components/BetweenItemDropBorder';
import { CUSTOM_DRAGGABLE_ITEM_TYPES } from 'components/customDragLayer/const';
import { useDrag } from 'components/customDragLayer/hooks/useDrag';

import { useReqsReviewConfigContext } from '../ReqsReviewConfigContext';
import { getAddQueryToGroupCascaderInfo } from '../utils/cascader';
import {
  type SignalWithId,
  type GroupWithId,
  buildFilterGroupFromCascaderValue,
  buildQueryFromCascaderValue,
} from '../utils/queryBuilding';
import { CountQuery, INITIAL_COUNT_QUERY_VALUE } from './CountQuery';
import { FilterQueryList } from './FilterQueryList';
import { type FormStateData } from './RulesDetailModal';
import { SelectedCallout } from './SelectedCallout';
import { SignalSelect } from './SignalSelect';

type FilterGroupComponentProps = {
  signal: SignalWithId;
  group: GroupWithId;
  groupIndex: number;
  signalIndex: number;
  isReadOnly: boolean;
  unit: PartFileUnits;
} & BoxProps;

export const FilterGroupComponent = ({
  signal,
  group,
  groupIndex,
  signalIndex,
  isReadOnly,
  unit,
  ...props
}: FilterGroupComponentProps) => {
  const { values, setFieldValue } = useFormikContext<FormStateData>();
  const signals = values.signals;
  const [showAddQueryCascader, setShowAddQueryCascader] = useState(false);
  const [isCascaderOpen, setIsCascaderOpen] = useState(false);
  const cascaderRef = useRef<HTMLInputElement>(null);

  const {
    configMap,
    labelPathMap,
    cascaderOptions,
    valueTypeToAvailableUnits,
  } = useReqsReviewConfigContext();

  const memoizedGroupInfo = useMemo(() => {
    if (!configMap) return { error: true } as const;

    const groupConfig = configMap[group.documentPath];
    if (!groupConfig) {
      console.warn(`Group config not found for fullPath ${group.documentPath}`);
      return { error: true } as const;
    }

    const newFieldCascaderInfo = getAddQueryToGroupCascaderInfo({
      group,
      configMap,
      parentCascaderOptions: cascaderOptions,
    });
    if (!newFieldCascaderInfo) {
      console.warn(
        `New field cascader info not found for group ${group.documentPath}`,
      );
      return { error: true } as const;
    }

    return {
      error: false,
      groupConfig,
      groupConfigPath: newFieldCascaderInfo.cascaderValue,
      newFieldCascaderOptions: newFieldCascaderInfo.cascaderOptions,
    } as const;
  }, [configMap, group, cascaderOptions]);

  const { groupConfig, groupConfigPath, newFieldCascaderOptions } =
    memoizedGroupInfo;

  // Drag functionality for filter groups
  const dragItem = useMemo(
    () => ({
      draggableItemType: CUSTOM_DRAGGABLE_ITEM_TYPES.RULES_FILTER_GROUP,
      description:
        labelPathMap && labelPathMap[group.documentPath]
          ? labelPathMap[group.documentPath]
          : `Filter group ${groupIndex + 1}`,
      group: {
        groupId: group.id,
        groupIndex,
        signalIndex,
        signalId: signal.id,
      },
    }),
    [
      group.id,
      groupIndex,
      signalIndex,
      signal.id,
      labelPathMap,
      group.documentPath,
    ],
  );

  const [{ isDragging }, connectDragSource] = useDrag({
    item: dragItem,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const handleFilterGroupChange = useCallback(
    (value: string[]) => {
      if (!configMap) return;

      const newGroup = buildFilterGroupFromCascaderValue({
        nextCascaderValue: value,
        configMap,
        valueTypeToAvailableUnits,
      });

      if (!newGroup) {
        console.warn(`New group not found for cascader value ${value}`);
        return;
      }

      // keep existing id
      newGroup.id = group.id;

      const updatedSignal = { ...signal };
      updatedSignal.groups[groupIndex] = newGroup;

      const newSignals = [...signals];
      newSignals[signalIndex] = updatedSignal;
      setFieldValue('signals', newSignals);
    },
    [
      configMap,
      signals,
      setFieldValue,
      signal,
      signalIndex,
      groupIndex,
      valueTypeToAvailableUnits,
      group.id,
    ],
  );

  const handleClearGroup = useCallback(() => {
    const newSignals = [...signals];
    const updatedSignal = { ...newSignals[signalIndex] };
    updatedSignal.groups.splice(groupIndex, 1);
    if (updatedSignal.groups.length === 0) {
      newSignals.splice(signalIndex, 1);
    } else {
      newSignals[signalIndex] = updatedSignal;
    }
    setFieldValue('signals', newSignals);
  }, [signals, setFieldValue, signalIndex, groupIndex]);

  const handleAddNewQuery = useCallback(
    (value: string[]) => {
      if (!configMap) return;

      const newQuery = buildQueryFromCascaderValue({
        nextCascaderValue: value,
        configMap,
        valueTypeToAvailableUnits,
      });

      if (!newQuery) {
        console.warn(`New query not found for cascader value ${value}`);
        return;
      }

      const newGroup = { ...group };
      newGroup.queries.push(newQuery);

      const updatedSignal = { ...signal };
      updatedSignal.groups[groupIndex] = newGroup;

      const newSignals = [...signals];
      newSignals[signalIndex] = updatedSignal;
      setFieldValue('signals', newSignals);

      // Close the cascader after adding a query
      setShowAddQueryCascader(false);
    },
    [
      signals,
      setFieldValue,
      configMap,
      valueTypeToAvailableUnits,
      group,
      signal,
      signalIndex,
      groupIndex,
    ],
  );

  const handleCountQueryChange = useCallback(
    (operator: CountOperator, value: number | RangeFilterValue) => {
      const updatedGroup = {
        ...group,
        countQuery: {
          operator,
          value,
        },
      };
      const updatedSignal = { ...signal };
      updatedSignal.groups[groupIndex] = updatedGroup;

      const newSignals = [...signals];
      newSignals[signalIndex] = updatedSignal;
      setFieldValue('signals', newSignals);
    },
    [signals, setFieldValue, group, signal, signalIndex, groupIndex],
  );

  const handleClearCountQuery = useCallback(() => {
    const updatedGroup = { ...group, countQuery: null };
    const updatedSignal = { ...signal };

    // If the updated group has no queries (since we just cleared the count query), we need to remove the group entirely
    if (updatedGroup.queries.length === 0) {
      updatedSignal.groups.splice(groupIndex, 1);
    } else {
      updatedSignal.groups[groupIndex] = updatedGroup;
    }

    const newSignals = [...signals];
    newSignals[signalIndex] = updatedSignal;
    setFieldValue('signals', newSignals);
  }, [signals, setFieldValue, group, signal, signalIndex, groupIndex]);

  // Auto-focus and open the cascader when it becomes visible
  // TODO: fix / improve this when new cascader is implemented
  useEffect(() => {
    if (showAddQueryCascader) {
      // Small delay to ensure the component is rendered
      const timer = setTimeout(() => {
        if (cascaderRef.current) {
          cascaderRef.current.focus();
        }
        setIsCascaderOpen(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setIsCascaderOpen(false);
    }
  }, [showAddQueryCascader]);

  const { selectedCalloutMeta } = useRulesDetailModalContext();

  const selectedCalloutMetaIds = useMemo(() => {
    if (!selectedCalloutMeta) {
      return null;
    }
    return {
      signalId: selectedCalloutMeta.signal?.id,
      groupIds: new Set(
        selectedCalloutMeta.signal?.groups.map((group) => group.id),
      ),
    };
  }, [selectedCalloutMeta]);

  if (memoizedGroupInfo.error || !configMap || !groupConfig) return null;

  const hideRemoveButton =
    group.queries.length === 0 &&
    group.countQuery === null &&
    groupIndex === signal.groups.length - 1;

  return (
    <ItemAndDropBorderContainer>
      <VStack
        spacing={1}
        align="stretch"
        className="filter-group-component"
        opacity={isDragging ? 0.5 : 1}
        {...props}
      >
        <VStack spacing={1} align="stretch" flex={1}>
          <HStack align="center">
            {!isReadOnly && (
              <IconButton ref={connectDragSource} size="md" cursor="grab">
                <DragHandleIcon />
              </IconButton>
            )}
            <VStack spacing={0} align="stretch" flex={1}>
              {selectedCalloutMeta &&
                selectedCalloutMetaIds?.signalId === signal.id &&
                selectedCalloutMetaIds?.groupIds.has(group.id) && (
                  <SelectedCallout
                    selectedCalloutMeta={selectedCalloutMeta}
                    onDeleteGroup={handleClearGroup}
                  />
                )}
              <SignalSelect
                isDisabled={isReadOnly}
                isClearable={true}
                placeholder="Add a group"
                value={groupConfigPath}
                options={cascaderOptions}
                onChange={handleFilterGroupChange}
                onClearSignal={handleClearGroup}
                signalIndex={signalIndex}
                groupIndex={groupIndex}
                className="filter-group-signal-select"
                hideRemoveButton={hideRemoveButton}
              />
            </VStack>
          </HStack>
          <Flex
            direction="column"
            gap={1}
            paddingLeft={3}
            className="filter-group-queries"
          >
            {group.queries.length > 0 && (
              <FilterQueryList
                group={group}
                groupIndex={groupIndex}
                signalIndex={signalIndex}
                signal={signal}
                isReadOnly={isReadOnly}
                unit={unit}
              />
            )}
            {groupConfig.countCompatible && group.countQuery && (
              <CountQuery
                operator={
                  group.countQuery?.operator ??
                  INITIAL_COUNT_QUERY_VALUE.operator
                }
                value={
                  group.countQuery?.value ?? INITIAL_COUNT_QUERY_VALUE.value
                }
                onChange={handleCountQueryChange}
                signalIndex={signalIndex}
                groupIndex={groupIndex}
                onClearCountQuery={handleClearCountQuery}
                hideRemoveButton={group.queries.length === 0}
              />
            )}
            {(newFieldCascaderOptions.children[0]?.children.length > 0 ||
              (groupConfig.countCompatible && !group.countQuery)) && (
              <HStack
                alignItems="flex-start"
                spacing={0}
                className="filter-group-actions"
              >
                <ArrowDownRightIcon
                  boxSize={2.5}
                  mr={1}
                  color="feature.placeholder"
                  className="filter-group-arrow"
                />
                <HStack spacing={1} className="filter-group-buttons">
                  {newFieldCascaderOptions.children[0]?.children.length > 0 &&
                    (showAddQueryCascader ? (
                      <SignalSelect
                        inputRef={cascaderRef}
                        placeholder="Add filter"
                        options={newFieldCascaderOptions}
                        isDisabled={isReadOnly}
                        onChange={handleAddNewQuery}
                        isClearable={false}
                        value={[]}
                        signalIndex={signalIndex}
                        groupIndex={groupIndex}
                        className="filter-group-add-query-select"
                        open={isCascaderOpen}
                        onPopupVisibleChange={setIsCascaderOpen}
                      />
                    ) : (
                      <Button
                        onClick={() => setShowAddQueryCascader(true)}
                        size="sm"
                        w="unset"
                        variant="secondary"
                        className="filter-group-add-query-button"
                      >
                        Add filter
                      </Button>
                    ))}
                  {groupConfig.countCompatible && !group.countQuery && (
                    <Tooltip label="Count of occurrences">
                      <Button
                        onClick={() => {
                          handleCountQueryChange(
                            INITIAL_COUNT_QUERY_VALUE.operator,
                            INITIAL_COUNT_QUERY_VALUE.value,
                          );
                        }}
                        size="sm"
                        w="unset"
                        variant="secondary"
                        className="filter-group-add-count-button"
                      >
                        Add count
                      </Button>
                    </Tooltip>
                  )}
                </HStack>
              </HStack>
            )}
          </Flex>
        </VStack>
      </VStack>
    </ItemAndDropBorderContainer>
  );
};
